import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import Dropdown from '../components/Dropdown';
import Modal from '../components/Modal';

import { useAppDispatch, useAppSelector } from '../hooks';

import { getClassNames } from '../../../utils';
import Loader from '../../../../web/images/svg/loader.svg';
import { updateSeats } from '../redux/sessionSlice';
import { useSelectSeatsMutation } from '../redux/tessituraApiSlice';
import { getFixedCachedKey } from '../utils';

const seatSelectionDropdownOptions = [...Array(11).keys()].map((index) => (
  { label: index.toString(), value: index.toString() }
));

const SeatSelectionForm = ({ handleApplyForm, setModal }: SeatSelectionFormProps) => {
  const [, { isLoading }] = useSelectSeatsMutation({ fixedCacheKey: getFixedCachedKey('select-seats') });
  const dispatch = useAppDispatch();

  const {
    packageLineNo,
    promoCode,
    seats,
  } = useAppSelector((state) => state.sessionSlice);

  const performances = useAppSelector((state) => state.packageDataSlice);

  const [isApplicable, setIsApplicable] = useState(false);

  useEffect(() => {
    if (seats > 0) setIsApplicable(true);
  }, [seats]);

  const updateCurrentSeatNumber = (number: string) => {
    const numberOfSeats = parseInt(number, 10);

    if (performances.length > 0) {
      setModal(
        <Modal
          onAccept={() => dispatch(updateSeats(numberOfSeats))}
          packageLineNumber={packageLineNo}
          setModal={setModal}
          type="reset-selection"
        />,
      );
    } else {
      dispatch(updateSeats(numberOfSeats));
    }

    if (numberOfSeats > 0) setIsApplicable(true);
  };

  const removePromoCode = () => {
    if (performances.length > 0) {
      setModal(
        <Modal
          onAccept={() => dispatch(updateSeats(seats))}
          packageLineNumber={packageLineNo}
          setModal={setModal}
          heading="To remove promo code, you'll need to reset your package"
          text={(
            <div>
              To correctly apply promo code pricing,
              you will need to recreate your package after removing promo code.
            </div>
          )}
          type="reset-selection"
        />,
      );
      setIsApplicable(true);
    } else {
      setModal(
        <Modal
          setModal={setModal}
          type="reset-promo-code"
        />,
      );
    }
  };

  const handleSetPromoCodeModal = () => {
    if (setModal) {
      document.body.classList.add('has-modal-opened');

      setModal(
        <Modal
          onAccept={() => {
            dispatch(updateSeats(seats));
          }}
          setModal={setModal}
          type="add-promo-code"
        />,
      );
    }
  };

  const handleAddPromoCodeClick = () => {
    document.body.classList.add('has-modal-opened');

    if (performances.length > 0) {
      setModal(
        <Modal
          onAccept={() => {
            dispatch(updateSeats(seats));
            handleSetPromoCodeModal();
            handleApplyForm();
          }}
          packageLineNumber={packageLineNo}
          setModal={setModal}
          heading="To apply promo code, you'll need to reset your package"
          text={(
            <div>
              To correctly apply promo code pricing,
              you will need to recreate your package after applying promo code.
            </div>
          )}
          type="reset-selection"
        />,
      );
      setIsApplicable(true);
    } else {
      handleSetPromoCodeModal();
    }
  };

  return (
    <div>
      <div
        className={getClassNames(null, 'c-cyop__seat-selection-form', {
          'c-cyop__seat-selection-form--loading': isLoading,
        })}
      >
        <div className="c-cyop__set-selection-form-element">
          <span className="c-cyop__seat-selection-form-label">Seat quantity</span>
          <div>
            <Dropdown
              id="seat-selection"
              label={seats.toString()}
              options={seatSelectionDropdownOptions}
              handleDropdownSelect={updateCurrentSeatNumber}
            />
          </div>
        </div>
        <div className="c-cyop__set-selection-form-element">
          <span className="c-cyop__seat-selection-form-label u-hide u-show@medium">&nbsp;</span>
          <div>
            <button
              className="c-button"
              disabled={!isApplicable || isLoading}
              onClick={handleApplyForm}
              onKeyDown={handleApplyForm}
              tabIndex={0}
              type="button"
            >
              Apply

              {isLoading && <div className="c-loader c-loader--button c-loader--dark c-loader--small"><Loader /></div>}
            </button>
          </div>
        </div>
      </div>
      {!promoCode && (
      <div className="c-cyop__seat-selection-form c-cyop__seat-selection-form--promo-code">
        <div className="c-cyop__set-selection-form-element">
          <p>
            Do you have a promo code? Please apply promo code&nbsp;
            <strong>before</strong>
            &nbsp;adding concerts to your pack.
          </p>
          <div>
            <button
              className="c-button c-button--promo-code"
              tabIndex={0}
              type="button"
              onClick={handleAddPromoCodeClick}
              onKeyDown={handleAddPromoCodeClick}
            >
              Submit promo code
            </button>
          </div>
        </div>
      </div>
      )}
      {promoCode
      && (
      <p className="c-cyop__promo-code-message">
        Promo Code
        {promoCode !== 'applied' ? ':' : ''}
        &nbsp;
        {promoCode}
        &nbsp;
        <a
          role="button"
          className="u-text-link u-text-link__promo-code"
          tabIndex={0}
          type="button"
          onClick={removePromoCode}
          onKeyDown={removePromoCode}
        >
          Clear
        </a>
      </p>
      )}
    </div>
  );
};

const seatSelectionFormPropTypes = {
  handleApplyForm: PropTypes.func.isRequired,
  setModal: PropTypes.func.isRequired,
};

SeatSelectionForm.propTypes = seatSelectionFormPropTypes;

type SeatSelectionFormProps = PropTypes.InferProps<typeof seatSelectionFormPropTypes>;

export default SeatSelectionForm;
